import { useState, useEffect } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { applySeo } from "@/lib/seo";
import { BarometerNav } from "@/components/BarometerNav";
import { BarometerHero } from "@/components/BarometerHero";
import { trpc } from "@/lib/trpc";
import { BAROMETER_PRICES, BAROMETER_MIT_VIDEOCALL, formatPrice } from "@shared/prices";
import { toast } from "sonner";
import { generateSeoPdf } from "@/lib/generatePdf";
import { CrossSelling } from "@/components/CrossSelling";

const PORTRAIT_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310519663476281654/fDRgangGWFphFKzWtNAEav/larissa-portrait_abbef783.jpg";
const MEETERGO_URL = "https://cal.meetergo.com/larissa-girenko/30-min-meeting-or-larissa-girenko?utm_source=ig&utm_medium=social&utm_content=link_in_bio";

const C = {
  green: "#0a5c36",
  greenDark: "#074228",
  greenLight: "#0a5c3615",
  bordeaux: "#6d0000",
  bg: "#ffffff",
  bgGray: "#f2f2f2",
  text: "#000000",
  muted: "#6b7280",
  border: "#e5e7eb",
};

const BRANCHEN = [
  { value: "", label: "Branche wählen (optional)" },
  { value: "dienstleister", label: "Dienstleister / Beratung" },
  { value: "ecommerce", label: "E-Commerce / Online-Shop" },
  { value: "gastronomie", label: "Gastronomie / Hotel / Tourismus" },
  { value: "gesundheit", label: "Gesundheit / Wellness / Beauty" },
  { value: "b2b", label: "B2B / Software / Technologie" },
  { value: "handwerk", label: "Handwerk / Bau / Immobilien" },
  { value: "kreativ", label: "Kreativbranche / Agentur / Design" },
  { value: "sonstige", label: "Sonstige" },
];

type SEODimension = {
  score: number;
  max: number;
  label: string;
  icon: string;
  beschreibung: string;
  details: Record<string, {
    score: number;
    max: number;
    found: boolean;
    issues: string[];
    [key: string]: unknown;
  }>;
};

type SEOData = {
  url: string;
  domain: string;
  title: string;
  meta_desc: string;
  screenshot_b64: string | null;
  total_score: number;
  max_score: number;
  percent: number;
  grade: string;
  dimensions: SEODimension[];
  empfehlungen: Array<{
    dimension: string;
    icon: string;
    prioritaet: "hoch" | "mittel";
    kriterium: string;
    massnahme: string;
  }>;
  pages_crawled: Array<{ url: string; type: string; title: string; error: string | null }>;
  load_time_ms: number;
  branche: string;
};

function ScoreBar({ score, max, color }: { score: number; max: number; color: string }) {
  const pct = max > 0 ? Math.round((score / max) * 100) : 0;
  return (
    <div className="w-full bg-gray-200 h-2 overflow-hidden">
      <div style={{ width: `${pct}%`, backgroundColor: color, height: "100%", transition: "width 0.6s ease" }} />
    </div>
  );
}

function getGradeColor(grade: string): string {
  switch (grade) {
    case "A": return "#16a34a";
    case "B": return "#0a5c36";
    case "C": return "#ca8a04";
    case "D": return "#ea580c";
    default: return "#dc2626";
  }
}

function getScoreColor(pct: number): string {
  if (pct >= 70) return "#16a34a";
  if (pct >= 45) return "#ca8a04";
  return "#dc2626";
}

function LoadingScreen({ url }: { url: string }) {
  const steps = [
    { label: "Website abrufen", icon: "/manus-storage/23_1ab7147e.svg" },
    { label: "Title & Meta-Tags prüfen", icon: "/manus-storage/icon_onpage_grundlagen_a1ddd99a.png" },
    { label: "Technische SEO analysieren", icon: "/manus-storage/23_1ab7147e.svg" },
    { label: "Ladegeschwindigkeit messen", icon: "/manus-storage/icon_mobile_technisch_9312395b.png" },
    { label: "Inhaltsqualität bewerten", icon: "/manus-storage/23_1ab7147e.svg" },
    { label: "Strukturierte Daten prüfen", icon: "/manus-storage/32_04885248.svg" },
    { label: "E-E-A-T-Signale auswerten", icon: "/manus-storage/icon_eeat_expertise_60d84526.png" },
    { label: "SEO-Score berechnen", icon: "/manus-storage/32_04885248.svg" },
  ];
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((s) => (s + 1) % steps.length);
    }, 1800);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white px-6">
      <div style={{ height: "4px", backgroundColor: C.green, position: "fixed", top: 0, left: 0, right: 0 }} />
      <div className="max-w-md w-full text-center">
        <div className="mb-8">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: C.greenLight }}>
            <img src="/manus-storage/23_1ab7147e.svg" alt="" className="w-10 h-10 object-contain mx-auto" style={{ opacity: 0.85 }} />
          </div>
          <p className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: C.green, fontFamily: "'DM Sans', sans-serif" }}>
            SEO-Barometer™ analysiert
          </p>
          <h2 className="font-black uppercase text-2xl mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            Deine Website wird geprüft
          </h2>
          <p className="text-gray-500 text-sm truncate" style={{ fontFamily: "'DM Sans', sans-serif" }}>{url}</p>
        </div>
        <div className="space-y-3 text-left mb-8">
          {steps.map((step, i) => (
            <div key={i} className="flex items-center gap-3 p-3" style={{ backgroundColor: i === activeStep ? C.greenLight : "transparent", borderLeft: i === activeStep ? `3px solid ${C.green}` : "3px solid transparent" }}>
              <img src={step.icon} alt="" className="w-5 h-5 object-contain flex-shrink-0" style={{ opacity: 0.85 }} />
              <span className="text-sm font-medium" style={{ color: i <= activeStep ? C.text : C.muted, fontFamily: "'DM Sans', sans-serif" }}>{step.label}</span>
              {i < activeStep && <span className="ml-auto text-green-600 text-sm">✓</span>}
              {i === activeStep && <span className="ml-auto text-xs animate-pulse" style={{ color: C.green }}>läuft …</span>}
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>
          Die Analyse dauert ca. 30–60 Sekunden. Bitte nicht schließen.
        </p>
      </div>
    </div>
  );
}

function SEOResultsView({ data, onReset }: { data: SEOData; onReset: () => void }) {
  // Defensive: empfehlungen und dimensions immer als Array garantieren
  const d = {
    ...data,
    empfehlungen: data.empfehlungen ?? [],
    dimensions: data.dimensions ?? [],
  };
  const [activeTab, setActiveTab] = useState<"uebersicht" | "dimensionen" | "empfehlungen">("uebersicht");
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (d.percent / 100) * circumference;
  const gradeColor = getGradeColor(d.grade);

  return (
    <div className="min-h-screen bg-white text-black">
      <div style={{ height: "4px", backgroundColor: C.green }} />
      <BarometerNav current="/seo-barometer" accentColor={"#0a5c36"} />

      <div className="bg-[#f2f2f2] border-b border-gray-200 px-6 py-3 flex items-center gap-3">
        <div className="w-2 h-2 bg-green-500 rounded-full" />
        <span className="text-gray-500 text-xs uppercase tracking-widest" style={{ fontFamily: "'DM Sans', sans-serif" }}>Analysiert:</span>
        <span className="text-black text-sm font-medium truncate" style={{ fontFamily: "'DM Sans', sans-serif" }}>{d.url}</span>
        {d.title && <span className="text-gray-400 text-xs hidden md:block">· {d.title}</span>}
      </div>

      <div className="max-w-5xl mx-auto px-6 py-10">
        {/* Score-Übersicht */}
        <div className="grid md:grid-cols-2 gap-8 mb-10">
          <div className="bg-[#f2f2f2] border border-gray-200 p-8 flex flex-col items-center justify-center text-center">
            <div className="relative mb-6">
              <svg className="w-40 h-40 -rotate-90" viewBox="0 0 120 120">
                <circle cx="60" cy="60" r={radius} fill="none" stroke="rgba(0,0,0,0.1)" strokeWidth="8" />
                <circle cx="60" cy="60" r={radius} fill="none" stroke={gradeColor} strokeWidth="8" strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-5xl font-black" style={{ fontFamily: "'Bebas Neue', sans-serif", color: gradeColor }}>{d.percent}</span>
                <span className="text-xs text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>von 100</span>
              </div>
            </div>
            <p className="text-sm font-bold mb-1" style={{ color: gradeColor, fontFamily: "'DM Sans', sans-serif" }}>
              {d.percent >= 80 ? "Exzellente SEO" : d.percent >= 60 ? "Gute SEO" : d.percent >= 40 ? "Ausbaufähige SEO" : d.percent >= 20 ? "Schwache SEO" : "Kritische SEO"}
            </p>
            <p className="text-xs text-gray-500 mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              {d.total_score} von {d.max_score} Punkten
            </p>
            <div className="grid grid-cols-2 gap-3 w-full text-left">
              <div className="p-3 bg-white border border-gray-200">
                <p className="text-[10px] uppercase tracking-widest text-gray-400 mb-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Ladezeit</p>
                <p className="font-black text-lg" style={{ fontFamily: "'Bebas Neue', sans-serif", color: d.load_time_ms <= 1500 ? "#16a34a" : d.load_time_ms <= 3000 ? "#ca8a04" : "#dc2626" }}>
                  {(d.load_time_ms / 1000).toFixed(1)}s
                </p>
              </div>
              <div className="p-3 bg-white border border-gray-200">
                <p className="text-[10px] uppercase tracking-widest text-gray-400 mb-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Domain</p>
                <p className="font-bold text-xs truncate" style={{ fontFamily: "'DM Sans', sans-serif" }}>{d.domain}</p>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            {d.screenshot_b64 && (
              <div className="border border-gray-200 overflow-hidden">
                <img src={`data:image/jpeg;base64,${d.screenshot_b64}`} alt="Website Screenshot" className="w-full object-cover" style={{ maxHeight: 200 }} />
              </div>
            )}
            <div className="bg-[#f2f2f2] border border-gray-200 p-5">
              <p className="text-[10px] uppercase tracking-widest font-bold mb-4" style={{ color: C.green, fontFamily: "'DM Sans', sans-serif" }}>SEO-Dimensionen auf einen Blick</p>
              <div className="space-y-2">
                {d.dimensions.map((dim) => {
                  const dp = dim.max > 0 ? Math.round((dim.score / dim.max) * 100) : 0;
                  return (
                    <div key={dim.label} className="flex items-center gap-2">
                      <img src={dim.icon} alt="" className="w-5 h-5 object-contain flex-shrink-0" style={{ opacity: 0.85 }} />
                      <span className="text-xs flex-1 truncate" style={{ fontFamily: "'DM Sans', sans-serif" }}>{dim.label}</span>
                      <span className="text-xs font-bold w-14 text-right" style={{ color: getScoreColor(dp), fontFamily: "'DM Sans', sans-serif" }}>{dim.score}/{dim.max}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-8 overflow-x-auto">
          {(["uebersicht", "dimensionen", "empfehlungen"] as const).map((tab) => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className="px-6 py-3 text-xs font-bold uppercase tracking-widest transition-colors whitespace-nowrap"
              style={{ color: activeTab === tab ? C.green : C.muted, borderBottom: activeTab === tab ? `2px solid ${C.green}` : "2px solid transparent", fontFamily: "'DM Sans', sans-serif" }}>
              {tab === "uebersicht" ? "📊 Übersicht" : tab === "dimensionen" ? "🔍 Dimensionen" : "💡 Empfehlungen"}
            </button>
          ))}
        </div>

        {/* Tab: Übersicht */}
        {activeTab === "uebersicht" && (
          <div className="space-y-4">
            {d.dimensions.map((dim) => {
              const dp = dim.max > 0 ? Math.round((dim.score / dim.max) * 100) : 0;
              const dc = getScoreColor(dp);
              return (
                <div key={dim.label} className="bg-[#f2f2f2] border border-gray-200 p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <img src={dim.icon} alt="" className="w-6 h-6 object-contain flex-shrink-0" style={{ opacity: 0.85 }} />
                      <div>
                        <span className="font-bold text-sm" style={{ fontFamily: "'DM Sans', sans-serif" }}>{dim.label}</span>
                        <p className="text-xs text-gray-500 mt-0.5 hidden sm:block" style={{ fontFamily: "'DM Sans', sans-serif" }}>{dim.beschreibung}</p>
                      </div>
                    </div>
                    <span className="text-sm font-black ml-4 whitespace-nowrap" style={{ color: dc, fontFamily: "'Bebas Neue', sans-serif" }}>{dim.score}/{dim.max}</span>
                  </div>
                  <ScoreBar score={dim.score} max={dim.max} color={dc} />
                </div>
              );
            })}
          </div>
        )}

        {/* Tab: Dimensionen */}
        {activeTab === "dimensionen" && (
          <div className="space-y-6">
            {d.dimensions.map((dim) => (
              <div key={dim.label} className="border border-gray-200">
                <div className="px-6 py-4 flex items-center gap-3" style={{ backgroundColor: C.bgGray, borderBottom: `1px solid ${C.border}` }}>
                  <img src={dim.icon} alt="" className="w-6 h-6 object-contain flex-shrink-0" style={{ opacity: 0.85 }} />
                  <h3 className="font-black text-base uppercase" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{dim.label}</h3>
                  <span className="ml-auto font-black text-lg" style={{ color: getScoreColor(dim.max > 0 ? Math.round(dim.score / dim.max * 100) : 0), fontFamily: "'Bebas Neue', sans-serif" }}>
                    {dim.score}/{dim.max}
                  </span>
                </div>
                <div className="divide-y divide-gray-100">
                  {Object.entries(dim.details).map(([key, detail]) => {
                    const dp = detail.max > 0 ? Math.round((detail.score / detail.max) * 100) : 0;
                    const dc = getScoreColor(dp);
                    return (
                      <div key={key} className="px-6 py-4">
                        <div className="flex items-start justify-between gap-4 mb-2">
                          <div className="flex items-center gap-2">
                            <span style={{ color: detail.found ? "#16a34a" : "#dc2626", fontSize: "1rem" }}>{detail.found ? "✓" : "✗"}</span>
                            <span className="text-sm font-medium capitalize" style={{ fontFamily: "'DM Sans', sans-serif" }}>{key.replace(/_/g, " ")}</span>
                          </div>
                          <span className="text-xs font-bold whitespace-nowrap" style={{ color: dc, fontFamily: "'DM Sans', sans-serif" }}>{detail.score}/{detail.max}</span>
                        </div>
                        {detail.issues.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {detail.issues.map((issue, i) => (
                              <p key={i} className="text-xs text-gray-600 flex items-start gap-1.5" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                                <span className="text-amber-500 mt-0.5">⚠</span>{issue}
                              </p>
                            ))}
                          </div>
                        )}
                        {/* Keyword-Fokus Details */}
                        {key === "keyword_fokus" && (detail as any).erkanntes_keyword && (
                          <div className="mt-2 p-3 bg-gray-50 border border-gray-100 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                            <p className="font-bold text-gray-800 mb-1">Erkanntes Keyword: <span style={{ color: C.green }}>{(detail as any).erkanntes_keyword}</span></p>
                            <div className="flex flex-wrap gap-3 text-gray-600">
                              <span>Title: {(detail as any).im_title ? "\u2713" : "\u2717"}</span>
                              <span>H1: {(detail as any).in_h1 ? "\u2713" : "\u2717"}</span>
                              <span>Meta: {(detail as any).in_meta ? "\u2713" : "\u2717"}</span>
                              <span>Text: {(detail as any).in_text ? "\u2713" : "\u2717"}</span>
                            </div>
                          </div>
                        )}
                        {/* Zielgruppen-Fragen Details */}
                        {key === "zielgruppen_fragen" && ((detail as any).beantwortet?.length > 0 || (detail as any).fehlend?.length > 0) && (
                          <div className="mt-2 p-3 bg-gray-50 border border-gray-100 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                            {(detail as any).beantwortet?.length > 0 && (
                              <p className="text-gray-700 mb-1"><span className="font-bold" style={{ color: "#16a34a" }}>Beantwortet:</span> {(detail as any).beantwortet.join(", ")}</p>
                            )}
                            {(detail as any).fehlend?.length > 0 && (
                              <p className="text-gray-700"><span className="font-bold" style={{ color: "#dc2626" }}>Fehlend:</span> {(detail as any).fehlend.join(", ")}</p>
                            )}
                          </div>
                        )}
                        {/* Textlaenge Details */}
                        {key === "textlaenge" && (detail as any).char_count != null && (
                          <div className="mt-2 p-3 bg-gray-50 border border-gray-100 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                            <p className="text-gray-700">
                              <span className="font-bold">{(detail as any).char_count?.toLocaleString()}</span> Zeichen
                              {(detail as any).word_count != null && <span> ({(detail as any).word_count?.toLocaleString()} Woerter)</span>}
                              {(detail as any).min_1800_chars === false && <span className="ml-2 font-bold" style={{ color: "#dc2626" }}> — unter 1.800 Zeichen Minimum</span>}
                              {(detail as any).min_1800_chars === true && <span className="ml-2 font-bold" style={{ color: "#16a34a" }}> — Minimum erreicht</span>}
                            </p>
                          </div>
                        )}
                        {/* Analytics/Tracking Details */}
                        {key === "analytics_tracking" && (detail as any).tool && (
                          <div className="mt-2 p-3 bg-gray-50 border border-gray-100 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                            <p className="text-gray-700">
                              Erkanntes Tool: <span className="font-bold" style={{ color: (detail as any).tool === "keins" ? "#dc2626" : "#16a34a" }}>{(detail as any).tool}</span>
                            </p>
                          </div>
                        )}
                        {/* URL-Qualität Details */}
                        {key === "url_qualitaet" && (detail as any).laenge != null && (
                          <div className="mt-2 p-3 bg-gray-50 border border-gray-100 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                            <div className="flex flex-wrap gap-3 text-gray-700">
                              <span>Länge: <span className="font-bold">{(detail as any).laenge}</span> Zeichen</span>
                              <span>Lesbar: {(detail as any).lesbar ? "\u2713" : "\u2717"}</span>
                              <span>Sonderzeichen: {(detail as any).sonderzeichen ? "\u2717 vorhanden" : "\u2713 keine"}</span>
                            </div>
                          </div>
                        )}
                        {/* noindex Details */}
                        {key === "noindex_check" && (detail as any).hinweis && (
                          <div className="mt-2 p-3 bg-gray-50 border border-gray-100 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                            <p className="text-gray-700">{(detail as any).hinweis}</p>
                          </div>
                        )}
                        {/* Open Graph Details */}
                        {key === "open_graph" && (
                          <div className="mt-2 p-3 bg-gray-50 border border-gray-100 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                            <div className="flex flex-wrap gap-3 text-gray-700">
                              <span>og:title: {(detail as any).og_title ? "\u2713" : "\u2717"}</span>
                              <span>og:description: {(detail as any).og_description ? "\u2713" : "\u2717"}</span>
                              <span>og:image: {(detail as any).og_image ? "\u2713" : "\u2717"}</span>
                            </div>
                          </div>
                        )}
                        {/* Bildformate Details */}
                        {key === "bildformate" && (detail as any).formate_gefunden && (
                          <div className="mt-2 p-3 bg-gray-50 border border-gray-100 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                            <p className="text-gray-700">
                              Formate: <span className="font-bold">{((detail as any).formate_gefunden || []).join(", ")}</span>
                              {(detail as any).webp_anteil != null && <span className="ml-2">| WebP-Anteil: <span className="font-bold">{(detail as any).webp_anteil}%</span></span>}
                            </p>
                          </div>
                        )}
                        {/* Breadcrumbs Details */}
                        {key === "breadcrumbs" && (
                          <div className="mt-2 p-3 bg-gray-50 border border-gray-100 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                            <p className="text-gray-700">
                              Schema BreadcrumbList: {(detail as any).schema_breadcrumb ? <span style={{ color: "#16a34a" }}>\u2713 vorhanden</span> : <span style={{ color: "#dc2626" }}>\u2717 fehlt</span>}
                            </p>
                          </div>
                        )}
                        {/* hreflang Details */}
                        {key === "hreflang" && (detail as any).sprachen?.length > 0 && (
                          <div className="mt-2 p-3 bg-gray-50 border border-gray-100 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                            <p className="text-gray-700">
                              Sprachen: <span className="font-bold">{((detail as any).sprachen || []).join(", ")}</span>
                            </p>
                          </div>
                        )}
                        {/* Popups/Overlays Details */}
                        {key === "popups_overlays" && (detail as any).typ && (detail as any).typ !== "keins" && (
                          <div className="mt-2 p-3 bg-gray-50 border border-gray-100 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                            <p className="text-gray-700">
                              Erkannt: <span className="font-bold" style={{ color: (detail as any).typ === "vollbild_overlay" ? "#dc2626" : "#d97706" }}>{(detail as any).typ?.replace(/_/g, " ")}</span>
                              {(detail as any).hinweis && <span className="ml-2 text-gray-500">({(detail as any).hinweis})</span>}
                            </p>
                          </div>
                        )}
                        {/* Alt-Texte Details */}
                        {key === "alt_texte" && (detail as any).bilder_gesamt != null && (
                          <div className="mt-2 p-3 bg-gray-50 border border-gray-100 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                            <p className="text-gray-700">
                              <span className="font-bold">{(detail as any).bilder_mit_alt ?? 0}</span> von <span className="font-bold">{(detail as any).bilder_gesamt}</span> Bildern mit Alt-Text
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Tab: Empfehlungen */}
        {activeTab === "empfehlungen" && (
          <div className="space-y-4">
            <div className="p-4 border-l-4 mb-6" style={{ borderColor: C.green, backgroundColor: C.greenLight }}>
              <p className="text-sm font-bold mb-1" style={{ color: C.green, fontFamily: "'DM Sans', sans-serif" }}>
                {(d.empfehlungen || []).filter(e => e.prioritaet === "hoch").length} kritische Maßnahmen identifiziert
              </p>
              <p className="text-xs text-gray-600" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                Priorisiere die rot markierten Punkte – sie haben den größten Einfluss auf dein Google-Ranking.
              </p>
            </div>
            {(d.empfehlungen || []).map((emp, i) => (
              <div key={i} className="border border-gray-200 p-5 flex items-start gap-4">
                <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center text-white text-xs font-bold"
                  style={{ backgroundColor: emp.prioritaet === "hoch" ? "#dc2626" : "#ca8a04" }}>
                  {i + 1}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <img src={emp.icon} alt="" className="w-5 h-5 object-contain flex-shrink-0" style={{ opacity: 0.85 }} />
                    <span className="text-xs font-bold uppercase tracking-widest" style={{ color: C.muted, fontFamily: "'DM Sans', sans-serif" }}>{emp.dimension}</span>
                    <span className="text-xs px-2 py-0.5 font-bold uppercase" style={{ backgroundColor: emp.prioritaet === "hoch" ? "#fef2f2" : "#fffbeb", color: emp.prioritaet === "hoch" ? "#dc2626" : "#ca8a04", fontFamily: "'DM Sans', sans-serif" }}>
                      {emp.prioritaet}
                    </span>
                  </div>
                  {emp.kriterium && (
                    <p className="text-sm font-bold text-gray-900 mb-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>{emp.kriterium}</p>
                  )}
                  <p className="text-sm text-gray-700 leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif" }}>{emp.massnahme}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* PDF-Download */}
        <div className="mt-8 flex justify-center">
          <button
            onClick={() => generateSeoPdf(d as unknown as Record<string, unknown>)}
            className="flex items-center gap-2 px-6 py-3 text-sm font-bold uppercase tracking-widest transition-all"
            style={{ backgroundColor: C.green, color: "#fff", fontFamily: "'DM Sans', sans-serif" }}
          >
            📄 PDF-Report herunterladen
          </button>
        </div>

        {/* CTA */}
        <div className="mt-8 text-center py-12 px-6" style={{ backgroundColor: C.green }}>
          <p className="text-xs font-bold tracking-widest uppercase mb-3 text-white opacity-70" style={{ fontFamily: "'DM Sans', sans-serif" }}>Weitere Barometer</p>
          <h3 className="font-black uppercase text-white text-3xl mb-6" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            Analysiere noch tiefer
          </h3>
          <div className="flex flex-wrap gap-3 justify-center">
            <a href="/sensorik" className="text-xs font-bold uppercase tracking-widest px-6 py-3 bg-white transition-colors" style={{ color: C.green, fontFamily: "'DM Sans', sans-serif" }}>🧠 Sensorik-Barometer</a>
            <a href="/lokal" className="text-xs font-bold uppercase tracking-widest px-6 py-3 border border-white text-white transition-colors" style={{ fontFamily: "'DM Sans', sans-serif" }}>📍 Lokal-Barometer</a>
            <a href="/geo-barometer" className="text-xs font-bold uppercase tracking-widest px-6 py-3 border border-white text-white transition-colors" style={{ fontFamily: "'DM Sans', sans-serif" }}>🤖 GEO-Barometer</a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SEOHome() {
  const { user } = useAuth();
  const isAdminUser = user?.role === "admin";
  useEffect(() => applySeo({
    title: "SEO-Barometer™ – Warum findet Google dich nicht? | WERBEAGENTUR Larissa Girenko",
    description: "100+ SEO-Kriterien: On-Page, Technik, E-E-A-T, Core Web Vitals. Persönliche Vollanalyse von Larissa ab 97 € – Lieferung in 72h. Kostenlose Vorschau sofort.",
    path: "/seo-barometer",
  }), []);
  const [url, setUrl] = useState("");
  const [branche, setBranche] = useState("");
  const [result, setResult] = useState<SEOData | null>(null);
  const [previewResult, setPreviewResult] = useState<Record<string, unknown> | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analyzingUrl, setAnalyzingUrl] = useState("");

  const analyzeMutation = trpc.seoBarometer.analyze.useMutation({
    onSuccess: (res) => {
      setIsLoading(false);
      if (res.success && res.data) {
        const raw = res.data as Record<string, unknown>;

        // --- Server→Frontend Mapping ---
        // Server gibt scores als Objekt zurück, Frontend erwartet dimensions als Array
        const scoresObj = (raw.scores ?? {}) as Record<string, Record<string, unknown>>;
        const dimIcons: Record<string, string> = {
          technische_seo: "/manus-storage/29_7f3bb394.svg",
          on_page_seo: "/manus-storage/32_04885248.svg",
          content_qualitaet: "/manus-storage/7_8bcad2d2.svg",
          lokale_seo: "/manus-storage/29_7f3bb394.svg",
          nutzererfahrung: "/manus-storage/32_04885248.svg",
        };
        const dimDescriptions: Record<string, string> = {
          technische_seo: "HTTPS, Indexierung, Ladezeit, Mobile, Canonical, Analytics, URL-Qualität",
          on_page_seo: "Title, Meta, H1, Headings, Alt-Texte, Open Graph, Bildformate",
          content_qualitaet: "Textlänge, Keywords, Zielgruppen-Fragen, Links, Breadcrumbs",
          lokale_seo: "NAP, Schema Markup, Google Maps, lokale Keywords, hreflang",
          nutzererfahrung: "Pop-ups, CTAs, Lesbarkeit & Textstruktur",
        };
        const dimensions: SEODimension[] = Object.entries(scoresObj).map(([key, dim]) => {
          const details = (dim.details ?? {}) as Record<string, Record<string, unknown>>;
          const mappedDetails: Record<string, { score: number; max: number; found: boolean; issues: string[]; [k: string]: unknown }> = {};
          for (const [dk, dv] of Object.entries(details)) {
            mappedDetails[dk] = {
              score: Number(dv.score ?? 0),
              max: Number(dv.max ?? 0),
              found: Boolean(dv.found ?? false),
              issues: [],
              ...dv,
            };
          }
          return {
            label: String(dim.label ?? key),
            score: Number(dim.score ?? 0),
            max: Number(dim.max ?? 0),
            icon: dimIcons[key] ?? "/manus-storage/29_7f3bb394.svg",
            beschreibung: dimDescriptions[key] ?? "",
            details: mappedDetails,
          };
        });

        // Ladezeit aus technische_seo.details.ladezeit.ms extrahieren
        const techSeo = scoresObj.technische_seo as Record<string, unknown> | undefined;
        const techDetails = (techSeo?.details ?? {}) as Record<string, Record<string, unknown>>;
        const ladezeitMs = Number(techDetails?.ladezeit?.ms ?? 0);

        // total_score und percent
        const totalScore = Number(raw.total_score ?? 0);
        const maxScore = dimensions.reduce((sum, d) => sum + d.max, 0) || 100;
        const percent = maxScore > 0 ? Math.round((totalScore / maxScore) * 100) : 0;

        // Grade berechnen
        const grade = percent >= 80 ? "A" : percent >= 65 ? "B" : percent >= 45 ? "C" : percent >= 25 ? "D" : "F";

        // Optimierungen → Empfehlungen
        const optis = (raw.optimierungen ?? raw.empfehlungen ?? []) as Array<Record<string, unknown>>;
        const empfehlungen = optis.map((o) => ({
          dimension: String(o.dimension ?? ''),
          icon: dimIcons[String(o.dimension ?? '').toLowerCase().replace(/[^a-z_]/g, '_')] ?? "/manus-storage/29_7f3bb394.svg",
          prioritaet: (o.prioritaet === 'hoch' ? 'hoch' : 'mittel') as 'hoch' | 'mittel',
          kriterium: String(o.kriterium ?? ''),
          massnahme: String(o.tipp ?? o.massnahme ?? o.kriterium ?? ''),
        }));

        const mapped: SEOData = {
          url: String(raw.url ?? ''),
          domain: String(raw.domain ?? ''),
          title: String(raw.page_title ?? ''),
          meta_desc: String((raw as Record<string, unknown>).meta_description ?? ''),
          screenshot_b64: null,
          total_score: totalScore,
          max_score: maxScore,
          percent,
          grade,
          dimensions,
          empfehlungen,
          pages_crawled: [],
          load_time_ms: ladezeitMs,
          branche: '',
        };
        setResult(mapped);
      } else {
        setError(res.error || "Unbekannter Fehler");
      }
    },
    onError: (err) => {
      setIsLoading(false);
      setError(err.message);
    },
  });

  // Preview-Mutation für kostenlose Vorschau (nur Scores)
  const previewMutation = trpc.seoBarometer.analyzePreview.useMutation({
    onSuccess: (res) => {
      setIsLoading(false);
      if (res.success && res.data) {
        setPreviewResult(res.data as Record<string, unknown>);
      } else {
        setError((res as Record<string, unknown>).error as string || "Unbekannter Fehler");
      }
    },
    onError: (err) => { setIsLoading(false); setError(err.message); },
  });

  const handleSubmit = () => {
    if (!url.trim()) return;
    setError(null);
    setResult(null);
    setPreviewResult(null);
    setAnalyzingUrl(url.trim());
    setIsLoading(true);
    if (isAdminUser) {
      analyzeMutation.mutate({ url: url.trim(), branche });
    } else {
      previewMutation.mutate({ url: url.trim() });
    }
  };
  const createCheckout = trpc.payment.createCheckout.useMutation({
    onSuccess: (data) => {
      if (data.checkoutUrl) window.open(data.checkoutUrl, "_blank");
    },
    onError: (err) => toast.error(err.message),
  });
  const handleBuy = () => {
    toast.info("Du wirst zu Stripe weitergeleitet …");
    createCheckout.mutate({
      origin: window.location.origin,
      productId: "seo_analyse",
      targetUrl: (previewResult as Record<string, unknown>)?.url as string || url.trim() || undefined,
      returnPath: "/seo-barometer",
    });
  };
  const handleBuyVideoCall = () => {
    toast.info("Du wirst zu Stripe weitergeleitet …");
    createCheckout.mutate({
      origin: window.location.origin,
      productId: "seo_mit_videocall",
      targetUrl: (previewResult as Record<string, unknown>)?.url as string || url.trim() || undefined,
      returnPath: "/seo-barometer",
    });
  };
  const handleReset = () => { setResult(null); setPreviewResult(null); setIsLoading(false); setUrl(""); setError(null); analyzeMutation.reset(); previewMutation.reset(); };
  const handleBooking = () => {
    const bookingUrl = url.trim()
      ? `${MEETERGO_URL}&notes=${encodeURIComponent("Website: " + url.trim())}`
      : MEETERGO_URL;
    window.open(bookingUrl, "_blank", "noopener,noreferrer");
  };

  if (isLoading) return <LoadingScreen url={analyzingUrl} />;
  if (result) return <SEOResultsView data={result} onReset={handleReset} />;

  return (
    <div className="min-h-screen bg-white text-black flex flex-col" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Akzentlinie */}
      <div style={{ height: "4px", backgroundColor: C.green }} />

      <BarometerNav current="/seo-barometer" accentColor={C.green} />

      <BarometerHero
        name="SEO-Barometer™"
        headline="Warum findet<br />Google dich nicht?"
        subtext={`100+ Kriterien · On-Page, Technik, E-E-A-T, Core Web Vitals. Persönliche Vollanalyse von Larissa ab ${formatPrice(BAROMETER_PRICES.seo)} · Lieferung in 72h.`}
        accentColor={C.green}
        ctaColor="#115751"
        stats={[
          { val: "7", label: "Dimensionen" },
          { val: "100+", label: "Kriterien" },
          { val: "60 Sek.", label: "Vorschau" },
        ]}
        url={url}
        onUrlChange={setUrl}
        onSubmit={handleSubmit}
        isLoading={isLoading}
        error={error}
        isAdmin={isAdminUser}
        adminCtaText="SEO-Vollanalyse starten →"
      />

      {/* Preview-Ergebnis: nur Scores, kein Empfehlungen-Tab */}
      {previewResult && (
        <section className="max-w-3xl mx-auto w-full px-6 py-12">
          <div className="border border-gray-200 p-6 mb-6" style={{ backgroundColor: C.bgGray }}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-2 h-2 rounded-full bg-green-500" />
              <p className="text-xs font-bold tracking-widest uppercase" style={{ color: C.green, fontFamily: "'DM Sans', sans-serif" }}>SEO-Barometer™ Vorschau</p>
              <span className="text-xs text-gray-400 truncate" style={{ fontFamily: "'DM Sans', sans-serif" }}>{analyzingUrl}</span>
            </div>
            {/* Gesamt-Score */}
            {(() => {
              const score = Number((previewResult as Record<string, unknown>).total_score ?? 0);
              const maxScore = Number((previewResult as Record<string, unknown>).max_score ?? 100);
              const pct = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
              const grade = pct >= 80 ? "A" : pct >= 65 ? "B" : pct >= 45 ? "C" : pct >= 25 ? "D" : "F";
              const gradeColor = getGradeColor(grade);
              return (
                <div className="flex items-center gap-6 mb-6 p-4 bg-white border border-gray-200">
                  <div className="text-center flex-shrink-0">
                    <div className="text-5xl font-black" style={{ fontFamily: "'Bebas Neue', sans-serif", color: gradeColor }}>{pct}</div>
                    <div className="text-xs text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>von 100</div>
                  </div>
                  <div>
                    <div className="text-lg font-black uppercase" style={{ fontFamily: "'Bebas Neue', sans-serif", color: gradeColor }}>
                      Note {grade} · {pct >= 80 ? "Exzellente SEO" : pct >= 60 ? "Gute SEO" : pct >= 40 ? "Ausbaufähige SEO" : pct >= 20 ? "Schwache SEO" : "Kritische SEO"}
                    </div>
                    <div className="text-sm text-gray-500 mt-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                      {score} von {maxScore} Punkten · {(previewResult as Record<string, unknown>).domain as string || analyzingUrl}
                    </div>
                  </div>
                </div>
              );
            })()}
            {/* Dimensions-Scores */}
            {(() => {
              const dims = (previewResult as Record<string, unknown>).dimension_scores as Array<{ label: string; score: number; max: number }> ?? [];
              return dims.length > 0 ? (
                <div className="space-y-2 mb-6">
                  <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: C.green, fontFamily: "'DM Sans', sans-serif" }}>Dimensionen auf einen Blick</p>
                  {dims.map((d) => {
                    const dp = d.max > 0 ? Math.round((d.score / d.max) * 100) : 0;
                    return (
                      <div key={d.label} className="flex items-center gap-3">
                        <span className="text-xs w-36 flex-shrink-0" style={{ fontFamily: "'DM Sans', sans-serif" }}>{d.label}</span>
                        <div className="flex-1 bg-gray-200 h-2">
                          <div style={{ width: `${dp}%`, backgroundColor: getScoreColor(dp), height: "100%" }} />
                        </div>
                        <span className="text-xs font-bold w-8 text-right" style={{ color: getScoreColor(dp), fontFamily: "'DM Sans', sans-serif" }}>{dp}%</span>
                      </div>
                    );
                  })}
                </div>
              ) : null;
            })()}
            {/* Upsell-CTA: zwei Optionen */}
            <div className="border-t border-gray-200 pt-5">
              <p className="text-xs font-bold tracking-widest uppercase mb-4 text-gray-500" style={{ fontFamily: "'DM Sans', sans-serif" }}>Nächster Schritt — Wähle deine Option</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="border-2 border-gray-200 p-5 flex flex-col">
                  <p className="text-xs font-bold tracking-widest uppercase mb-1 text-gray-500" style={{ fontFamily: "'DM Sans', sans-serif" }}>Option 1</p>
                  <h3 className="font-black uppercase text-lg mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Vollständige Analyse</h3>
                  <p className="text-gray-600 text-xs mb-3 flex-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Alle Befunde, priorisierte Empfehlungen und persönlicher PDF-Report. Lieferung in 72 Stunden.</p>
                  <div className="text-xl font-black mb-3" style={{ fontFamily: "'Bebas Neue', sans-serif", color: C.green }}>{formatPrice(BAROMETER_PRICES.seo)}</div>
                  <button onClick={handleBuy} className="w-full text-white font-bold text-xs uppercase tracking-widest px-4 py-3 hover:opacity-90 transition-opacity" style={{ backgroundColor: C.green, fontFamily: "'DM Sans', sans-serif" }}>Analyse buchen</button>
                </div>
                <div className="border-2 p-5 flex flex-col relative" style={{ borderColor: "#6d0000", backgroundColor: "#6d000008" }}>
                  <div className="absolute -top-3 left-4 px-3 py-1 text-white text-xs font-bold uppercase tracking-widest" style={{ backgroundColor: "#6d0000", fontFamily: "'DM Sans', sans-serif" }}>Empfohlen</div>
                  <p className="text-xs font-bold tracking-widest uppercase mb-1" style={{ color: "#6d0000", fontFamily: "'DM Sans', sans-serif" }}>Option 2</p>
                  <h3 className="font-black uppercase text-lg mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Analyse + VideoCall</h3>
                  <p className="text-gray-600 text-xs mb-3 flex-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Vollständige Analyse + 30-minütige persönliche Ergebnis-Interpretation mit Larissa per VideoCall.</p>
                  <div className="text-xl font-black mb-1" style={{ fontFamily: "'Bebas Neue', sans-serif", color: "#6d0000" }}>{formatPrice(BAROMETER_MIT_VIDEOCALL)}</div>
                  <p className="text-xs text-gray-400 mb-3" style={{ fontFamily: "'DM Sans', sans-serif" }}>inkl. 30-Min-VideoCall (+79 €)</p>
                  <button onClick={handleBuyVideoCall} className="w-full text-white font-bold text-xs uppercase tracking-widest px-4 py-3 hover:opacity-90 transition-opacity" style={{ backgroundColor: "#6d0000", fontFamily: "'DM Sans', sans-serif" }}>Analyse + VideoCall buchen</button>
                </div>
              </div>
              <button onClick={handleReset} className="block w-full text-center text-xs text-gray-400 mt-2 hover:text-gray-600" style={{ fontFamily: "'DM Sans', sans-serif" }}>Andere URL analysieren</button>
            </div>
          </div>
        </section>
      )}

      {/* Was wird geprüft (nur wenn kein Preview-Ergebnis) */}
      {!previewResult && (
      <section className="py-16 px-6" style={{ backgroundColor: C.bgGray, borderTop: `1px solid ${C.border}` }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: C.green, fontFamily: "'DM Sans', sans-serif" }}>7 Dimensionen · 100+ Kriterien</p>
            <h2 className="font-black uppercase text-black" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(1.8rem, 3.5vw, 2.5rem)" }}>
              Was der SEO-Grundlagen-Check prüft
            </h2>
            <div className="mt-4 max-w-2xl mx-auto border-l-4 px-4 py-3 text-left text-xs" style={{ borderColor: C.green, backgroundColor: "#f0fdf4", color: C.muted }}>
              <p><strong style={{ color: C.green }}>ℹ️ Umfang & Grenzen:</strong> Dieser Check analysiert alle <strong>technisch messbaren On-Page- und Technik-Faktoren</strong> deiner Website. <strong>Nicht enthalten:</strong> Backlink-Profil, Domain Authority, Keyword-Rankings, Wettbewerbs-Analyse oder Suchvolumen-Daten – diese erfordern spezialisierte SEO-Tools (z. B. Ahrefs, Semrush). Für eine vollständige SEO-Strategie ist eine individuelle Beratung empfehlenswert.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[
              { icon: "/manus-storage/icon_onpage_grundlagen_a1ddd99a.png", label: "On-Page Grundlagen", desc: "Title-Tag, Meta Description, H1–H4 Hierarchie", max: 30 },
              { icon: "/manus-storage/23_1ab7147e.svg", label: "Technische SEO", desc: "HTTPS, Canonical, Robots, Viewport, Charset, Lang", max: 30 },
              { icon: "/manus-storage/icon_mobile_technisch_9312395b.png", label: "Ladegeschwindigkeit", desc: "Core Web Vitals, Lazy Loading, Render-Blocking, Komprimierung", max: 20 },
              { icon: "/manus-storage/23_1ab7147e.svg", label: "Inhaltsqualität", desc: "Wortanzahl, Keyword-Relevanz, Alt-Texte, interne Links", max: 25 },
              { icon: "/manus-storage/32_04885248.svg", label: "Strukturierte Daten", desc: "JSON-LD Schema, Open Graph, Twitter Cards", max: 15 },
              { icon: "/manus-storage/icon_eeat_expertise_60d84526.png", label: "E-E-A-T Signale", desc: "Impressum, Datenschutz, Autorenangaben, Vertrauen", max: 15 },
              { icon: "/manus-storage/icon_seitenstruktur_087c12d9.png", label: "Seitenstruktur", desc: "Hreflang, Sitemap, Breadcrumbs, Navigation", max: 15 },
            ].map((item) => (
              <div key={item.label} className="bg-white border border-gray-200 p-5">
                <div className="flex items-start justify-between mb-3">
                  <img src={item.icon} alt="" className="w-7 h-7 object-contain flex-shrink-0" style={{ opacity: 0.85 }} />
                  <span className="text-xs font-bold text-gray-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>max. {item.max} Pkt.</span>
                </div>
                <h3 className="font-black text-sm uppercase mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{item.label}</h3>
                <p className="text-xs text-gray-500 leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif" }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Über Larissa */}
      <div className="mt-16 max-w-5xl w-full mx-auto px-6 mb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 bg-black overflow-hidden">
          <div className="relative" style={{ minHeight: "440px" }}>
            <img src={PORTRAIT_IMG} alt="Larissa Girenko – SEO-Expertin" className="w-full h-full object-cover object-top" style={{ minHeight: "440px" }} />
          </div>
          <div className="flex flex-col justify-center px-10 py-12 bg-black">
            <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: C.green, fontFamily: "'DM Sans', sans-serif" }}>Über die Entwicklerin</p>
            <h2 className="font-black uppercase text-white leading-tight mb-6" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)" }}>
              Larissa Girenko –<br />
              <span style={{ color: C.green }}>SEO trifft Sensorik</span>
            </h2>
            <p className="text-gray-300 text-sm leading-relaxed mb-5" style={{ fontFamily: "'DM Sans', sans-serif" }}>
Viele Unternehmen scheitern bei Google nicht an schlechten Inhalten, sondern an fehlenden Grundlagen – technischen Fehlern, die unsichtbar bleiben, bis der Wettbewerber sie ausnutzt. Der SEO-Barometer macht diese Schwachstellen sichtbar.
            </p>
            <p className="text-gray-300 text-sm leading-relaxed mb-8" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Der SEO-Barometer prüft deine Website auf alle relevanten Ranking-Faktoren nach dem Stand 2025/2026 – von technischen Grundlagen bis zu E-E-A-T-Signalen, die Google für Vertrauenswürdigkeit bewertet.
            </p>
            <div className="border-t border-gray-700 pt-6">
              <p className="text-gray-400 text-xs italic" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                „SEO ohne Messung ist Marketing ohne Kompass."
              </p>
              <p className="text-xs font-bold uppercase tracking-widest mt-2" style={{ color: C.green, fontFamily: "'DM Sans', sans-serif" }}>
                Larissa Girenko · Werbeagentur Larissa Girenko
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Cross-Selling */}
      <CrossSelling
        headline="SEO gecheckt – jetzt auch sichtbar werden"
        subline="Ergänze das SEO-Barometer mit diesen Analysen für maximale digitale Sichtbarkeit."
        items={[
          {
            icon: "/manus-storage/7_8bcad2d2.svg",
            title: "GEO-Barometer™",
            description: "KI-Sichtbarkeit in ChatGPT, Perplexity und Google AI – der nächste Schritt nach klassischem SEO.",
            price: formatPrice(BAROMETER_PRICES.geo),
            href: "/geo-barometer",
            accent: "#0a5c36",
            badge: "Ergänzt SEO",
          },
          {
            icon: "/manus-storage/1_55991aa3.svg",
            title: "Lokal-Barometer™",
            description: "Lokale Sichtbarkeit in Google Maps und lokalen Suchanfragen – für regionale Unternehmen.",
            price: formatPrice(BAROMETER_PRICES.lokal),
            href: "/lokal-barometer",
            accent: "#c2410c",
          },
          {
            icon: "/manus-storage/33_d51b7819.svg",
            title: "Conversion-Barometer™",
            description: "Mehr Traffic bringt nichts ohne Conversions. Psychologische Hebel für mehr Umsatz.",
            price: formatPrice(BAROMETER_PRICES.conversion),
            href: "/conversion-barometer",
            accent: "#b45309",
          },
        ]}
      />

      {/* Footer */}
      <footer className="border-t border-gray-200 px-6 py-6 text-center mt-4 bg-white">
        <div style={{ height: "3px", background: `linear-gradient(to right, ${C.green} 40%, ${C.bordeaux} 100%)`, marginBottom: "1rem" }} />
        <p className="text-xs" style={{ color: C.muted }}>
          © 2026 WERBEAGENTUR Larissa Girenko · SEO-Barometer™ · Dein Schlüssel zum Marketingerfolg
        </p>
        <p className="text-xs mt-2">
          <a href="/impressum" className="hover:underline" style={{ color: C.bordeaux }}>Impressum</a>
          <span className="mx-2" style={{ color: C.muted }}>·</span>
          <a href="/datenschutz" className="hover:underline" style={{ color: C.bordeaux }}>Datenschutzerklärung</a>
        </p>
      </footer>
    </div>
  );
}
